/**
 * StoredDocumentsPanel — Phase 70G "Uploaded statements" tab.
 *
 * Shows the user every PDF we currently hold for them, with a clear
 * scheduled-deletion timestamp + a Delete-now button per row.
 *
 * Trust principle (the reason this exists):
 *   Most fintechs are opaque about what files they're holding. We're
 *   not. This panel says, in plain language: here is exactly what we
 *   have, here is when we'll automatically destroy it, and here is a
 *   button to destroy it right now.
 *
 * The panel intentionally renders a friendly empty state when nothing
 * is held — that is the *expected* steady state, not an error.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  FileText, Trash2, Shield, Clock, CheckCircle2, AlertTriangle,
  Hourglass, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function freshToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch { return null; }
}

// Map retention bucket → human label, badge colour, icon.
const BUCKET_META = {
  consumed: {
    label: 'Imported',
    Icon: CheckCircle2,
    badgeClass: 'border-emerald-500/40 text-emerald-600 dark:text-emerald-400',
    why: 'You imported the parsed transactions. The original PDF is no longer needed.',
  },
  failed: {
    label: 'Parse failed',
    Icon: AlertTriangle,
    badgeClass: 'border-red-500/40 text-red-600 dark:text-red-400',
    why: 'This upload could not be parsed. We delete it quickly because it has no value to either of us.',
  },
  orphan: {
    label: 'Awaiting review',
    Icon: Hourglass,
    badgeClass: 'border-amber-500/40 text-amber-600 dark:text-amber-400',
    why: 'Parsed but never imported. Held for a few days in case you come back to it.',
  },
  no_transactions: {
    label: 'No transactions',
    Icon: AlertTriangle,
    badgeClass: 'border-amber-500/40 text-amber-700 dark:text-amber-300',
    why: "This looks like a holdings snapshot, not a transaction statement. Try downloading 'Activity' or 'Transaction history' instead.",
  },
  active: {
    label: 'Processing',
    Icon: Loader2,
    badgeClass: 'border-sky-500/40 text-sky-600 dark:text-sky-400',
    why: 'Currently being parsed. You can still delete it any time.',
  },
  unknown: {
    label: 'Held',
    Icon: Shield,
    badgeClass: 'border-muted-foreground/40 text-muted-foreground',
    why: 'Held in storage.',
  },
};

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function fmtCountdown(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return '';
  const abs = Math.abs(ms);
  const mins = Math.round(abs / 60000);
  const hrs  = Math.round(abs / 3600000);
  const days = Math.round(abs / 86400000);
  let human = '';
  if (abs < 60_000)         human = 'momentarily';
  else if (mins < 60)       human = `${mins} min`;
  else if (hrs  < 48)       human = `${hrs} h`;
  else                      human = `${days} days`;
  return ms < 0 ? `${human} ago` : `in ${human}`;
}

function DocumentRow({ doc, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const meta = BUCKET_META[doc.retention_bucket] || BUCKET_META.unknown;
  const KIcon = meta.Icon;
  // Phase 71.0 — users own their PDFs. They can delete at any time,
  // regardless of processing status. The endpoint already supports this;
  // the UI just needs to expose it. This also unblocks "stuck"
  // documents that for any reason never transition out of `active`.
  const canDelete = !busy;
  const isHoldingsSnapshot = doc.retention_bucket === 'no_transactions';

  const handleDelete = async () => {
    setBusy(true);
    try {
      const token = await freshToken();
      if (!token) throw new Error('Sign in to delete.');
      const r = await axios.post(
        `${BACKEND_URL}/api/import/jobs/${doc.job_id}/delete-pdf`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
      );
      if (r.data?.already_deleted) {
        toast.info('That file was already deleted.');
      } else {
        toast.success('PDF deleted. Your transactions are unaffected.');
      }
      onDeleted?.(doc.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not delete that file.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-border/60 bg-background/60"
      data-testid="stored-document-row"
    >
      <div className="flex items-center justify-between gap-3 px-3 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-lg bg-muted grid place-items-center shrink-0">
            <FileText className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate flex items-center gap-2">
              {doc.filename || 'Unnamed statement'}
              <Badge variant="outline" className={`rounded-full text-[10px] px-1.5 ${meta.badgeClass}`} data-testid="stored-document-status-badge">
                <KIcon className={`h-2.5 w-2.5 mr-1 ${doc.retention_bucket === 'active' ? 'animate-spin' : ''}`} />
                {meta.label}
              </Badge>
            </div>
            <div className="text-[11px] text-muted-foreground truncate flex items-center gap-2 mt-0.5">
              <span>Uploaded {fmtDate(doc.uploaded_at)}</span>
              {doc.imported_at && <span>· Imported {fmtDate(doc.imported_at)}</span>}
            </div>
            {doc.scheduled_deletion_at && (
              <div className="text-[11px] text-foreground/70 flex items-center gap-1 mt-1">
                <Clock className="h-3 w-3 shrink-0" />
                <span>
                  Scheduled deletion: <span className="font-medium">{fmtDate(doc.scheduled_deletion_at)}</span>
                  {' '}
                  <span className="text-muted-foreground">({fmtCountdown(doc.scheduled_deletion_at)})</span>
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0">
          <AlertDialog>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!canDelete}
                      className="rounded-lg text-xs text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                      data-testid="stored-document-delete-button"
                      aria-label={`Delete ${doc.filename || 'document'} now`}
                    >
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
                      Delete now
                    </Button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-xs">
                  {meta.why}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <AlertDialogContent data-testid="stored-document-delete-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this PDF permanently?</AlertDialogTitle>
                <AlertDialogDescription>
                  The original document for <span className="font-medium">{doc.filename || 'this statement'}</span> will be removed from our storage immediately. <span className="font-medium">Your imported transactions will not be affected.</span> This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="stored-document-delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="stored-document-delete-confirm-button"
                >
                  Delete PDF
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Phase 71.0 — Holdings snapshot banner. Shown inline under the
          row so the user can see exactly which file is the snapshot
          alongside the explanation + delete action. */}
      {isHoldingsSnapshot && (
        <div
          className="mx-3 mb-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3 text-[12px] text-amber-900 dark:text-amber-200"
          data-testid="holdings-snapshot-banner"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="leading-relaxed">
              <span className="font-medium">This looks like a holdings snapshot, not a transaction statement.</span>{' '}
              We found 0 transactions in it. Try downloading <span className="font-medium">&ldquo;Activity&rdquo;</span> or <span className="font-medium">&ldquo;Transaction history&rdquo;</span> from your brokerage instead — those export the trades, dividends, and fees we can categorise.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function StoredDocumentsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const token = await freshToken();
      if (!token) { setLoading(false); return; }
      const r = await axios.get(`${BACKEND_URL}/api/import/stored-documents`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setData(r.data);
    } catch (_e) {
      setData({ documents: [], policy: null, storage_note: '' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDeleted = (jobId) => {
    setData((prev) => prev ? {
      ...prev,
      documents: (prev.documents || []).filter((d) => d.job_id !== jobId),
    } : prev);
  };

  if (loading) {
    return (
      <Card className="rounded-2xl border-border/70" data-testid="stored-documents-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  const docs = data?.documents || [];
  const note = data?.storage_note || '';

  return (
    <div className="space-y-4" data-testid="stored-documents-panel">
      {/* Trust banner — explains the policy in plain English. */}
      <Card className="rounded-2xl border-primary/30 bg-primary/[0.04]">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/15 grid place-items-center shrink-0">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold mb-1">Our minimal retention promise</div>
              <p className="text-[12px] text-foreground/80 leading-relaxed">
                {note || (
                  "Once a statement is imported, we delete the original statement file within 24 hours. "
                  + "Failed imports go within 24 hours; abandoned uploads within 7 days. "
                  + "Your transactions stay; the original file doesn't."
                )}
              </p>
              <p className="text-[11px] text-muted-foreground mt-2">
                Duplicate-upload detection uses a SHA-256 hash that we keep indefinitely — we never need the file body for that.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents list. */}
      <Card className="rounded-2xl border-border/70">
        <CardContent className="p-5">
          <div className="flex items-baseline justify-between mb-4">
            <div className="text-sm font-semibold">
              Uploaded documents
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                {docs.length} held
              </span>
            </div>
            <button
              type="button"
              onClick={() => { setLoading(true); load(); }}
              className="text-[11px] text-muted-foreground hover:text-foreground"
              data-testid="stored-documents-refresh"
            >
              Refresh
            </button>
          </div>

          {docs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/60 bg-muted/10 px-5 py-8 text-center" data-testid="stored-documents-empty">
              <div className="h-12 w-12 mx-auto mb-3 rounded-2xl bg-emerald-500/10 grid place-items-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="text-sm font-medium">No statements currently stored.</div>
              <p className="text-[12px] text-muted-foreground mt-1">
                This is the expected state. PDFs are automatically deleted shortly after use; only your transactions remain.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map((doc) => (
                <DocumentRow key={doc.job_id} doc={doc} onDeleted={handleDeleted} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
